/**
 * usePlaidConnectionStatus — Listen to bank connection metadata
 *
 * Reads from plaid_meta subcollection (client-readable).
 * Detects connections that need re-authentication.
 * Returns active connections list for Studio management UI.
 */

import { useState, useEffect } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase/config';

export interface PlaidConnection {
  itemId: string;
  institutionName: string;
  lastSyncAt: number | null;
  connectedAt: number | null;
  status: string | null;
}

interface PlaidConnectionStatus {
  /** All active (non-disconnected) connections */
  connections: PlaidConnection[];
  /** True if any connection needs re-authentication */
  needsReauth: boolean;
  /** Item ID of the first connection needing re-auth */
  reauthItemId: string | null;
  /** Institution name of the connection needing re-auth */
  reauthInstitutionName: string | null;
  /** True while loading initial data */
  isLoading: boolean;
}

export function usePlaidConnectionStatus(userId: string | undefined): PlaidConnectionStatus {
  const [connections, setConnections] = useState<PlaidConnection[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setConnections([]);
      setIsLoading(false);
      return;
    }

    const colRef = collection(db, 'users', userId, 'plaid_meta');
    const unsub = onSnapshot(
      colRef,
      (snap) => {
        const conns: PlaidConnection[] = [];
        for (const doc of snap.docs) {
          const data = doc.data();
          // Skip disconnected connections
          if (data.status === 'disconnected') continue;
          conns.push({
            itemId: doc.id,
            institutionName: data.institutionName || 'Bank',
            lastSyncAt: data.lastSyncAt || null,
            connectedAt: data.connectedAt || null,
            status: data.status || 'connected',
          });
        }
        setConnections(conns);
        setIsLoading(false);
      },
      (err) => {
        console.error('[usePlaidConnectionStatus] Error:', err);
        setIsLoading(false);
      }
    );

    return unsub;
  }, [userId]);

  const reauthConn = connections.find((c) => c.status === 'requires_reauth') || null;

  return {
    connections,
    needsReauth: reauthConn !== null,
    reauthItemId: reauthConn?.itemId || null,
    reauthInstitutionName: reauthConn?.institutionName || null,
    isLoading,
  };
}
